import moment from 'moment-timezone';
import { FC, useCallback, useMemo, type JSX } from 'react';

import { currencyPrice } from '@/helpers/financeHelper';
import LabelValuePair from '@payaca/components/labelValuePair/LabelValuePair';
import { getAddressAsString } from '@payaca/helpers/locationHelper';
import { formatPhoneNumber } from '@payaca/helpers/phoneNumberHelper';

import AssignedUsersIndicator from '@payaca/components/assignedUsersIndicator/AssignedUsersIndicator';
import StatusBadge from '@payaca/components/statusBadge/StatusBadge';
import {
  DateFormats,
  getInternationalMomentDateFormatByRegion,
} from '@payaca/helpers/internationalHelper';
import { ListedDeal } from '@payaca/types/listedDealTypes';
import { isNullish } from '@payaca/utilities/guards';
import { Link } from 'react-router-dom';
import { useAccount } from '@/api/rest/users/useAccount';
import { useAccountUsers } from '@/api/queries/me/useAccountUsers';
import { useRegion } from '@/api/rest/users/useRegion';
import ListedDealInfoIcons from '../listedDealInfoIcons/ListedDealInfoIcons';
import { PipelineBadge } from '../pipelineBadge/PipelineBadge';

type Props = {
  collapse?: boolean;
  listedDeal: ListedDeal;
  listedDealIndex: number;
  onClickRow: (DealId: number) => void;
  hideCustomerColumn?: boolean;
};
const ListedDealsTableRow: FC<Props> = ({
  listedDeal,
  listedDealIndex,
  onClickRow,
  collapse,
  hideCustomerColumn,
}: Props): JSX.Element => {
  const account = useAccount();
  const region = useRegion();
  const accountUsers = useAccountUsers();

  const renderCustomerTableCell = useCallback(
    (rowSpan: number) => {
      return (
        <td className="customer-table-cell" rowSpan={rowSpan}>
          <Link
            to={`/projects/${listedDeal.dealId}`} // TODO: should use project publicId
            className="text-inherit hover:no-underline"
          >
            <strong className="customer-contact-name">
              {listedDeal.customerName}
              {listedDeal.contactName &&
              listedDeal.contactName !== listedDeal.customerName
                ? `: ${listedDeal.contactName}`
                : ''}
            </strong>
          </Link>
          {listedDeal.contactEmail && <small>{listedDeal.contactEmail}</small>}
          {listedDeal.contactPhoneNumber && (
            <small>{formatPhoneNumber(listedDeal.contactPhoneNumber)}</small>
          )}
        </td>
      );
    },
    [listedDeal]
  );

  const renderReferenceTableCell = useCallback(() => {
    return (
      <td className="reference-table-cell">
        {listedDeal.reference}
        {!hideCustomerColumn && (
          <strong className="customer-contact-name">
            {listedDeal.customerName}
          </strong>
        )}
      </td>
    );
  }, [hideCustomerColumn, listedDeal]);

  const renderAddressesTableCell = useCallback(
    (rowSpan: number) => {
      const hasAnySiteAddresses = !!listedDeal.siteAddresses?.length;
      const billingAddressAsString =
        listedDeal.customerBillingAddress &&
        getAddressAsString(listedDeal.customerBillingAddress);
      const hasBillingAddress =
        billingAddressAsString && billingAddressAsString.length > 0;

      return (
        <td rowSpan={rowSpan} className="addresses-table-cell">
          {!hasAnySiteAddresses && hasBillingAddress && (
            <small>{billingAddressAsString}</small>
          )}
          {hasAnySiteAddresses && hasBillingAddress && (
            <small>
              <LabelValuePair label="Billing" value={billingAddressAsString} />
            </small>
          )}
          {hasAnySiteAddresses && (
            <small>
              <LabelValuePair
                label="Site"
                value={`${getAddressAsString(listedDeal.siteAddresses[0])}${
                  listedDeal.siteAddresses.length > 1
                    ? ` (+ ${listedDeal.siteAddresses.length - 1} more)`
                    : ''
                }`}
              />
            </small>
          )}
        </td>
      );
    },
    [listedDeal]
  );

  const renderCollapsedTableRow = useMemo(() => {
    return (
      <>
        <tbody
          className={'listed-deal-table-row-container clickable'}
          onClick={() => onClickRow(listedDeal.dealId)}
          key={`${listedDeal.dealId}-${listedDealIndex}-body`}
        >
          <tr>
            {renderReferenceTableCell()}
            <td className="text-right!">
              {isNullish(listedDeal.dealValue)
                ? '-'
                : currencyPrice(listedDeal.dealValue, region)}
            </td>
            <td>
              <PipelineBadge pipelineId={listedDeal.pipelineId} />
            </td>
            <td>
              <StatusBadge>{listedDeal.pipelineStage}</StatusBadge>
            </td>
          </tr>
        </tbody>
      </>
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'onClickRow'
  }, [listedDeal, listedDealIndex, renderReferenceTableCell, account]);

  const shortDateRegionalFormat = useMemo(
    () => getInternationalMomentDateFormatByRegion(DateFormats.SHORT, region),
    [region]
  );

  const assignedUser = useMemo(() => {
    if (listedDeal.assignedToUserId) {
      return accountUsers.find(
        (user) => user.id === listedDeal.assignedToUserId
      );
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'listedDeal.assignedToUserId'
  }, [accountUsers]);

  const renderTableRow = useMemo(() => {
    return (
      <>
        <tbody
          className={'listed-deal-table-row-container clickable'}
          onClick={() => onClickRow(listedDeal.dealId)}
        >
          <tr>
            <td className="text-center!">
              <strong>{listedDeal.reference}</strong>
            </td>
            {!hideCustomerColumn && renderCustomerTableCell(0)}
            {renderAddressesTableCell(0)}
            <td className="text-center! description-table-cell" rowSpan={0}>
              <small>{listedDeal.description}</small>
            </td>
            <td className="text-center!">
              {moment(listedDeal.lastUpdatedAt).format(shortDateRegionalFormat)}
            </td>
            <td className="text-right!">
              {isNullish(listedDeal.dealValue)
                ? '-'
                : currencyPrice(listedDeal.dealValue, region)}
            </td>
            <td>
              <PipelineBadge pipelineId={listedDeal.pipelineId} />
            </td>
            <td>
              <StatusBadge>{listedDeal.pipelineStage}</StatusBadge>
            </td>
            <td className="text-center!">
              {assignedUser && (
                <AssignedUsersIndicator
                  assignedUsers={[
                    {
                      firstName: assignedUser.firstname,
                      lastName: assignedUser.lastname,
                      emailAddress: assignedUser.email,
                      userColour: assignedUser.userColour,
                      imgSrc: assignedUser.avatarUrl,
                    },
                  ]}
                />
              )}
            </td>
            <td className="text-right!">
              <ListedDealInfoIcons listedDeal={listedDeal} />
            </td>
          </tr>
        </tbody>
      </>
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has missing dependencies: 'assignedUser.avatarUrl', 'assignedUser.firstname', 'assignedUser', 'assignedUser.lastname', 'onClickRow', 'assignedUser.email', and 'assignedUser.userColour'
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: listedDealIndex
  }, [
    listedDeal,
    listedDealIndex,
    renderAddressesTableCell,
    renderCustomerTableCell,
    account,
    shortDateRegionalFormat,
    hideCustomerColumn,
  ]);

  return collapse ? renderCollapsedTableRow : renderTableRow;
};

export default ListedDealsTableRow;
